/**
 * Player Segment Library Service
 *
 * Manages notable segments saved to a player's personal library for reuse across lessons.
 *
 * Firestore structure:
 *   players/{playerId}/savedSegments/{segmentId}
 */

/**
 * Special player ID used by the admin test page (VideoDraftTest) so that coaches
 * can test the save-to-library feature without a real player context.
 * Segments saved here are stored under players/admin-test-player/savedSegments/.
 */
export const ADMIN_TEST_PLAYER_ID = 'admin-test-player';

import {
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  orderBy,
  query,
} from 'firebase/firestore';
import { db } from '../firebase';
import type { NotableSegment, SavedPlayerSegment, VideoContext } from '../types/videoEdit';

function savedSegmentsCol(playerId: string) {
  return collection(db, 'players', playerId, 'savedSegments');
}

/**
 * Upsert a single segment using the segment's own ID as the document ID.
 * Safe to call on every auto-save — creates or overwrites the same document.
 */
export async function upsertSegmentToPlayerLibrary(
  segment: NotableSegment,
  playerId: string,
  context: VideoContext,
  originalVideoId: string,
  coachId: string,
  coachName: string,
  lessonId?: string,
): Promise<void> {
  const raw: SavedPlayerSegment = {
    id: segment.id,
    label: segment.label,
    playerId,
    context,
    originalVideoId,
    startTime: segment.startTime,
    endTime: segment.endTime,
    savedAt: new Date().toISOString(),
    savedBy: coachId,
    savedByName: coachName,
    ...(segment.note !== undefined && { note: segment.note }),
    ...(lessonId !== undefined && { lessonId }),
  };
  // Strip undefined values (Firestore rejects them)
  const entry = Object.fromEntries(
    Object.entries(raw).filter(([, v]) => v !== undefined),
  ) as SavedPlayerSegment;
  await setDoc(doc(savedSegmentsCol(playerId), segment.id), entry);
}

/**
 * Sync all segments for a lesson to the player library in parallel.
 * Skips segments with externalSource (those originated from another player/library).
 */
export async function syncSegmentsToPlayerLibrary(
  segments: NotableSegment[],
  playerId: string,
  context: VideoContext,
  originalVideoId: string,
  coachId: string,
  coachName: string,
  lessonId?: string,
): Promise<void> {
  const local = segments.filter((s) => !s.externalSource);
  await Promise.all(
    local.map((seg) =>
      upsertSegmentToPlayerLibrary(seg, playerId, context, originalVideoId, coachId, coachName, lessonId),
    ),
  );
}

export async function getPlayerSavedSegments(playerId: string): Promise<SavedPlayerSegment[]> {
  const q = query(savedSegmentsCol(playerId), orderBy('savedAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => d.data() as SavedPlayerSegment);
}

export async function updateSavedSegment(
  playerId: string,
  id: string,
  update: Partial<Pick<SavedPlayerSegment, 'label' | 'note'>>,
): Promise<void> {
  const clean = Object.fromEntries(Object.entries(update).filter(([, v]) => v !== undefined));
  await updateDoc(doc(savedSegmentsCol(playerId), id), clean);
}

export async function deleteSavedSegment(playerId: string, id: string): Promise<void> {
  await deleteDoc(doc(savedSegmentsCol(playerId), id));
}
